import { IModelActionsHandler, ICreateNodeTemplateResponse } from "./ah.mb.common.model-actions-handler";
import { Clipboard } from "../ah.mb.clipboard";
import { modelServices } from "../ah.mb.ui.model-services";
import { ClientNotifier, IBasicCommandResponse } from "../ah.mb.client-notifications";
import { bootbox } from "../../infrastructure/bootbox-shim";
import { NodeObject, INodeObject } from "../common/ah.mb.common.node-object";
import { NodeCopyPasteData, INodeDiagramPosition, IKinkPoint } from "./ah.mb.ui.model-observer";
import { LinkProperty } from "../ah.mb.model-utils";

export class DefaultModelActionsHandler implements IModelActionsHandler {

    private readonly clipboard: Clipboard;
    private readonly modelServices: modelServices;
    private readonly modelId: string;
    userNotifier: ClientNotifier;

    /**
     * Ctor
     * @param modelId
     * @param modelServices
     * @param userNotifier
     * @param clipboard
     */
    constructor(modelId: string,
        modelServices: modelServices,
        userNotifier: ClientNotifier,
        clipboard: Clipboard) {
        this.clipboard = clipboard;
        this.modelId = modelId;
        this.modelServices = modelServices;
        this.userNotifier = userNotifier;

    }
    setKinkPoints(positions: IKinkPoint[]): void {
        this.modelServices.setKinkPoints(this.modelId, positions);
    }

    /**
     * Creates a node
     * @param nodeName
     * @param path
     * @param nodeType
     * @param initData
     */
    createNode(path: string, nodeName: string, nodeType: string, initData: Object): Promise<IBasicCommandResponse> {
        return this.modelServices.createNode(this.modelId,
            path,
            nodeName,
            nodeType,
            initData,
            () => { }
        )

    }

    /**
     * Deletes nodes
     * @param nodes
     */
    deleteNodes(nodes: NodeObject[], options?: { bypassRecycleBin: boolean }): Promise<IBasicCommandResponse> {
        var notification = "";



        if (nodes.length == 0) {
            bootbox.alert("No nodes selected");
            return Promise.reject();
        }
        else if (nodes.length == 1) {

            let deleteStr = `Delete ${nodes[0].name}?`;
            if (options && options.bypassRecycleBin)
                deleteStr = `Permanently delete ${nodes[0].name}?`;

            return bootbox.confirm2(deleteStr)
                .then(() => {
                    notification = this.userNotifier.showBusyNotification("Deleting nodes...");
                    return this.modelServices.deleteNodes(this.modelId, nodes, options);
                })
                .then(response => {
                    this.userNotifier.clear(notification);
                    return response;
                });
        }
        else {
            let deleteStr = `Delete ${nodes.length.toString()} nodes?`;
            if (options && options.bypassRecycleBin)
                deleteStr = `Permanently delete ${nodes.length.toString()} nodes?`;


            return bootbox.confirm2(deleteStr)
                .then(() => {
                    notification = this.userNotifier.showBusyNotification("Deleting nodes...");
                    return this.modelServices.deleteNodes(this.modelId, nodes, options);

                })
                .then(response => {
                    this.userNotifier.clear(notification);
                    return response;
                });

        };
    }

    /**
     * Renames a node
     * @param nodePathAndName
     * @param newVal
     */
    renameNode(nodePathAndName: string, newVal?: string): Promise<IBasicCommandResponse> {

        var showPrompt = newVal === undefined;

        const executeRename = (newName: string): Promise<IBasicCommandResponse> => {

            var notification =
                this.userNotifier.showNotification(`Renaming ${nodePathAndName}`,
                    true,
                    true);

            return this.modelServices.renameNode(this.modelId,
                nodePathAndName,
                newName)
                .then((ev) => {
                    this.userNotifier.clear(notification);
                    return ev;
                });

        }

        if (showPrompt) {
            return bootbox.prompt({
                title: `Rename ${nodePathAndName}`,
                content: NodeObject.extractName(nodePathAndName),
                callback:
                    newName => {
                        if (newName) {
                            executeRename(newName)

                        }
                    }
            });
        }
        else
            return executeRename(newVal);

    }

    /**
     * Copies nodes to clipboard
     * @param isCut
     * @param nodeFullPaths
     */
    copyNodesToClipboard(isCut: boolean, nodeFullPaths: string[]): void {
        const copyData = new NodeCopyPasteData(this.modelId,
            isCut,
            nodeFullPaths
        );

        this.clipboard.copyToClipboard(JSON.stringify(copyData));
        this.userNotifier.showSimpleMessage(copyData.nodeFullPaths.length.toString() + " nodes on clipboard");
    }

    /**
     * Pastes nodes from the clipboard
     * @param targetNode 
     * @param clipboardText 
     * @returns {} 
     */
    pasteNodesFromClipboard(targetNode: string, clipboardText: string, subNodesToInclude: string[]): Promise<IBasicCommandResponse> {

            var clipboardData;

            try {
                clipboardData = JSON.parse(clipboardText);
            } catch (e) {
                console.log();
                return Promise.reject({
                    success: false,
                    response: "Node list not found on clipboard"
                });
            }

            // Sometimes shit from spreadsheet copying shows up in here - have to ake sure it is
            // nodes
            if (clipboardData.sourceModelId === undefined)
                return Promise.reject({
                    success: false,
                    response: "Source model id not found in clipboard data"
                });

            // Get target path
            //var targetPath = internalGetTreeNodePath(targetNode, false);

            var notification = this.userNotifier.showBusyNotification("Pasting nodes...");

            return this.modelServices.pasteNodes(
                clipboardData.sourceModelId,
                this.modelId,
                clipboardData.nodeFullPaths,
                targetNode,
                subNodesToInclude,
                clipboardData.isCut)
                .then((data) => {
                    this.userNotifier.clear(notification);
                    return data;
                });
        };


    /**
     * Sets node positions
     * @param positions
     */
    setNodePositions(positions: INodeDiagramPosition[]): Promise<IBasicCommandResponse> {

        return this.modelServices.setNodePositions(this.modelId, positions);
    }

    /**
     * Creates a link
     * @param sourceNode 
     * @param destNode 
     * @param commodity 
     * @param units 
     * @param properties 
     * @returns {} 
    */
    createLink = (sourceNode: string,
        destNode: string,
        commodity: string,
        units: string,
        properties: LinkProperty[]): Promise<IBasicCommandResponse> => {

        return this.modelServices.createLink(this.modelId,
            sourceNode,
            destNode,
            commodity,
            units,
            properties);
    }

    /**
     * Deletes a link
     * @param sourceNode 
     * @param destNode 
     * @returns {} 
    */
    deleteLink = (sourceNode: string, destNode: string): Promise<IBasicCommandResponse> => {
        return this.modelServices.deleteLink(this.modelId, sourceNode, destNode);
    }

    /**
* Edits a link
* @param sourceNode 
* @param destNode 
* @param commodity 
* @param units 
* @param properties 
* @returns {} 
*/
    editLink = (sourceNode: string,
        destNode: string,
        commodity: string,
        units: string,
        properties: LinkProperty[]): Promise<IBasicCommandResponse> => {

        return this.modelServices.editLink(this.modelId, sourceNode, destNode, commodity, units, properties);
    }

    /**
* gets a node data value
* @param node 
* @param variableName 
* @returns {} 
*/
    getNodeDataValue = (node: string, variableName: string): Promise<any> => {
        return this.modelServices.getNodeDataValue(this.modelId, node, variableName);
    }

    groupNodes = (nodes: INodeObject[], groupName: string) =>
        this.modelServices.groupNodes(this.modelId, nodes, groupName);

    ungroupNodes = (node: string): Promise<IBasicCommandResponse> =>
        this.modelServices.ungroupNodes(this.modelId, node);


    createNodeTemplate(nodes: Object[], templateName: string): Promise<ICreateNodeTemplateResponse> {
        return this.modelServices.createNodeTemplate(this.modelId, nodes, templateName);
    }

    addNodeFromTemplate(parentNodePathAndName: string, templateNodePathAndName: string, initData: any): Promise<IBasicCommandResponse> {
        return this.modelServices.addNodeFromTemplate(this.modelId, parentNodePathAndName, templateNodePathAndName,
            initData);
    }

}



// WEBPACK FOOTER //
// ./ClientApp/scripts/model-notifier/ah.mb.common.default-model-actions-handler.ts